<script setup>
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useInbox } from 'dashboard/composables/useInbox';
import { useMessagingWindowTimer } from 'dashboard/composables/useMessagingWindowTimer';
import { hasMessagingWindowTimer } from 'dashboard/helper/messagingWindowHelper';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const { inbox, isAWhatsAppChannel } = useInbox();

const expiresAt = computed(
  () => props.conversation.messaging_window_expires_at
);

const { formattedTime, isExpired, urgency } =
  useMessagingWindowTimer(expiresAt);

const showTimer = computed(() => {
  if (!props.conversation.can_reply || !expiresAt.value) {
    return false;
  }

  if (isAWhatsAppChannel.value) {
    return true;
  }

  return hasMessagingWindowTimer(inbox.value?.channelType, inbox.value?.medium);
});

const urgencyClasses = computed(() => {
  const classes = {
    normal: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6',
    warning: 'bg-n-amber-3 text-n-amber-11 border-n-amber-6',
    critical: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-6',
  };

  return classes[urgency.value];
});

watch(isExpired, expired => {
  if (!expired) return;

  store.commit('SET_CONVERSATION_CAN_REPLY', {
    conversationId: props.conversation.id,
    canReply: false,
  });
});
</script>

<template>
  <div
    v-show="showTimer"
    class="mx-3 mt-2 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs"
    :class="urgencyClasses"
  >
    <span class="i-lucide-timer shrink-0 text-sm" />
    <span class="font-medium">
      {{ t('CONVERSATION.MESSAGING_WINDOW_TIMER.LABEL') }}
    </span>
    <span class="font-mono text-sm font-semibold tabular-nums">
      {{ formattedTime }}
    </span>
    <span class="text-[11px] opacity-80">
      {{ t('CONVERSATION.MESSAGING_WINDOW_TIMER.HINT') }}
    </span>
  </div>
</template>
